import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
];

const CATEGORY_ICONS = {
  office_rent: 'fa-building',
  salaries: 'fa-users',
  utilities: 'fa-bolt',
  supplies: 'fa-box',
  travel: 'fa-car',
  marketing: 'fa-bullhorn',
  maintenance: 'fa-wrench',
  gst_paid: 'fa-percent',
  miscellaneous: 'fa-ellipsis-h',
  custom: 'fa-tag',
};

const CATEGORY_COLORS = {
  office_rent: 'bg-purple-100 text-purple-700',
  salaries: 'bg-blue-100 text-blue-700',
  utilities: 'bg-yellow-100 text-yellow-700',
  supplies: 'bg-green-100 text-green-700',
  travel: 'bg-orange-100 text-orange-700',
  marketing: 'bg-pink-100 text-pink-700',
  maintenance: 'bg-gray-100 text-gray-700',
  gst_paid: 'bg-rose-100 text-rose-700',
  miscellaneous: 'bg-indigo-100 text-indigo-700',
  custom: 'bg-teal-100 text-teal-700',
};

export default function ExpenseManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  
  // Filters — default to current calendar month so expense list + summary are monthly-scoped
  const _exp_today = new Date();
  const _exp_monthStart = new Date(_exp_today.getFullYear(), _exp_today.getMonth(), 1);
  const _exp_monthEnd = new Date(_exp_today.getFullYear(), _exp_today.getMonth() + 1, 0);
  const _exp_fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFromDate, setFilterFromDate] = useState(_exp_fmt(_exp_monthStart));
  const [filterToDate, setFilterToDate] = useState(_exp_fmt(_exp_monthEnd));
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [showManageCategoriesDialog, setShowManageCategoriesDialog] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    category: '',
    custom_category: '',
    amount: '',
    description: '',
    payment_mode: 'bank_transfer',
    vendor_name: '',
    reference_number: '',
  });
  
  const [newCategoryName, setNewCategoryName] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiClient.get('/finance/expenses/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());
      if (filterCategory && filterCategory !== 'all') params.append('category', filterCategory);
      if (filterFromDate) params.append('from_date', filterFromDate);
      if (filterToDate) params.append('to_date', filterToDate);
      
      const response = await apiClient.get(`/finance/expenses?${params}`);
      setExpenses(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        total_pages: response.data.pagination?.total_pages || 0
      }));
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterCategory, filterFromDate, filterToDate]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterFromDate) params.append('from_date', filterFromDate);
      if (filterToDate) params.append('to_date', filterToDate);
      
      const response = await apiClient.get(`/finance/expenses/summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [filterFromDate, filterToDate]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
  }, [fetchExpenses, fetchSummary]);

  const handleAddExpense = async () => {
    if (!form.category || !form.amount || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const payload = {
        date: form.date,
        category: form.category,
        custom_category: form.category === 'custom' ? form.custom_category : null,
        amount: parseFloat(form.amount),
        description: form.description,
        payment_mode: form.payment_mode,
        vendor_name: form.vendor_name || null,
        reference_number: form.reference_number || null,
      };
      
      await apiClient.post('/finance/expenses', payload);
      toast.success('Expense added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    }
  };

  const handleEditExpense = async () => {
    if (!selectedExpense) return;
    
    try {
      const payload = {
        date: form.date,
        category: form.category,
        custom_category: form.category === 'custom' ? form.custom_category : null,
        amount: parseFloat(form.amount),
        description: form.description,
        payment_mode: form.payment_mode,
        vendor_name: form.vendor_name || null,
        reference_number: form.reference_number || null,
      };
      
      await apiClient.put(`/finance/expenses/${selectedExpense.id}`, payload);
      toast.success('Expense updated successfully');
      setShowEditDialog(false);
      resetForm();
      setSelectedExpense(null);
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`Delete expense: ${expense.description}?`)) return;
    
    try {
      await apiClient.delete(`/finance/expenses/${expense.id}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      await apiClient.post(`/finance/expenses/categories?name=${encodeURIComponent(newCategoryName)}`);
      toast.success('Category added');
      setShowAddCategoryDialog(false);
      setNewCategoryName('');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleRenameCategory = async (categoryId) => {
    const trimmed = (editingCategoryName || '').trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    try {
      await apiClient.patch(`/finance/expenses/categories/${categoryId}`, { name: trimmed });
      toast.success('Category renamed');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      fetchCategories();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename category');
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Deactivate "${cat.label}"? Past expenses will keep this name but the category will be hidden from new expenses. Continue?`)) {
      return;
    }
    try {
      const r = await apiClient.delete(`/finance/expenses/categories/${cat.id}`);
      const usage = r?.data?.expenses_using_this_category ?? 0;
      toast.success(`Deactivated. ${usage} past expense${usage === 1 ? '' : 's'} retain this name.`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const openEditDialog = (expense) => {
    setSelectedExpense(expense);
    setForm({
      date: expense.date ? new Date(expense.date).toLocaleDateString('en-CA') : '',
      category: expense.category,
      custom_category: expense.custom_category || '',
      amount: expense.amount.toString(),
      description: expense.description,
      payment_mode: expense.payment_mode || 'bank_transfer',
      vendor_name: expense.vendor_name || '',
      reference_number: expense.reference_number || '',
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setForm({
      date: new Date().toLocaleDateString('en-CA'),
      category: '',
      custom_category: '',
      amount: '',
      description: '',
      payment_mode: 'bank_transfer',
      vendor_name: '',
      reference_number: '',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getCategoryLabel = (category, customCategory) => {
    if (category === 'custom' && customCategory) return customCategory;
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  };

  return (
    <div className="space-y-3 sm:space-y-4 p-2 sm:p-4 md:p-6" data-testid="expense-management">
      {/* Header — consistent dark sticky pattern */}
      <div className="sticky top-0 z-10 rounded-xl p-3 sm:p-4 shadow-sm" style={{ backgroundColor: '#192650' }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance')}
                    className="h-9 px-2 text-white/80 hover:text-white hover:bg-white/10"
                    data-testid="expense-back-btn">
              <i className="fas fa-arrow-left mr-1" />
              <span className="hidden sm:inline text-sm">Back</span>
            </Button>
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#0DA891' }}>
              <i className="fas fa-receipt text-white text-base sm:text-lg" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-white leading-tight">Expense Management</h1>
              <p className="text-[11px] sm:text-xs text-white/70 hidden sm:block">Office, salaries, marketing, GST and more</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button onClick={() => setShowManageCategoriesDialog(true)}
                    variant="outline"
                    className="h-9 text-sm px-2.5 sm:px-3 border-slate-300 text-slate-700 hover:bg-slate-50 shrink-0"
                    data-testid="expense-manage-categories-btn">
              <i className="fas fa-cog sm:mr-1.5" /> <span className="hidden sm:inline">Manage Categories</span>
            </Button>
            <Button onClick={() => setShowAddDialog(true)}
                    className="h-9 flex-1 sm:flex-none text-sm px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="expense-add-btn">
              <i className="fas fa-plus mr-1.5" /> Add Expense
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards — readable on mobile */}
      {summary && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-red-700 leading-tight">{formatCurrency(summary.grand_total)}</p>
              <p className="text-[11px] sm:text-xs text-red-600/80 font-medium mt-0.5">Total Expenses</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-blue-700 leading-tight">{summary.total_count}</p>
              <p className="text-[11px] sm:text-xs text-blue-600/80 font-medium mt-0.5">Entries</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border-purple-200">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-base sm:text-2xl font-bold text-purple-700 leading-tight">{summary.by_category?.length || 0}</p>
              <p className="text-[11px] sm:text-xs text-purple-600/80 font-medium mt-0.5">Categories</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tap-to-filter category strip — tap a category to filter the list */}
      {summary && summary.by_category?.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-0.5" data-testid="expense-category-strip">
          <button
            type="button"
            onClick={() => setFilterCategory('all')}
            data-testid="expense-cat-chip-all"
            className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterCategory === 'all' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
          >
            All · {formatCurrency(summary.grand_total)}
          </button>
          {summary.by_category.map((c) => (
            <button
              key={c._id || 'uncat'}
              type="button"
              onClick={() => setFilterCategory(c._id)}
              data-testid={`expense-cat-chip-${c._id}`}
              className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterCategory === c._id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'}`}
            >
              {getCategoryLabel(c._id)} · {formatCurrency(c.total)} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-2.5 sm:p-3">
          <div className="space-y-2 sm:space-y-0 sm:flex sm:flex-wrap sm:items-end sm:gap-2">
            <div className="w-full sm:flex-1 sm:min-w-[120px]">
              <Label className="text-[10px] text-gray-500 block mb-0.5">Category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9 text-sm w-full" data-testid="expense-category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value + (cat.custom_value || '')} value={cat.value || 'custom'}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:contents">
              <div className="sm:flex-1 sm:min-w-[120px]">
                <Label className="text-[10px] text-gray-500 block mb-0.5">From</Label>
                <Input 
                  type="date" 
                  value={filterFromDate} 
                  onChange={(e) => setFilterFromDate(e.target.value)}
                  className="h-9 text-sm w-full"
                />
              </div>
              <div className="sm:flex-1 sm:min-w-[120px]">
                <Label className="text-[10px] text-gray-500 block mb-0.5">To</Label>
                <Input 
                  type="date" 
                  value={filterToDate} 
                  onChange={(e) => setFilterToDate(e.target.value)}
                  className="h-9 text-sm w-full"
                />
              </div>
            </div>
            {(filterCategory !== 'all' || filterFromDate || filterToDate) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-9 text-xs text-gray-500 hover:text-red-500 w-full sm:w-auto"
                onClick={() => { setFilterCategory('all'); setFilterFromDate(''); setFilterToDate(''); }}
              >
                <i className="fas fa-rotate-left mr-1.5"></i> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Expense List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <i className="fas fa-spinner fa-spin text-xl text-teal-600"></i>
        </div>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <i className="fas fa-receipt text-3xl text-gray-300 mb-3"></i>
            <p className="text-gray-500 text-sm">No expenses found</p>
            <Button onClick={() => setShowAddDialog(true)} className="mt-3 h-8 text-xs">
              <i className="fas fa-plus mr-1"></i> Add First Expense
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {expenses.map((expense) => (
            <Card key={expense.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${CATEGORY_COLORS[expense.category] || CATEGORY_COLORS.custom}`}>
                      <i className={`fas ${CATEGORY_ICONS[expense.category] || CATEGORY_ICONS.custom} text-xs`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-gray-900 truncate">{expense.description}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <Badge className={`text-[10px] ${CATEGORY_COLORS[expense.category] || CATEGORY_COLORS.custom}`}>
                          {getCategoryLabel(expense.category, expense.custom_category)}
                        </Badge>
                        <span className="text-[10px] text-gray-500">{formatDate(expense.date)}</span>
                        {expense.vendor_name && (
                          <span className="text-[10px] text-gray-500">• {expense.vendor_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-red-600 text-sm">{formatCurrency(expense.amount)}</p>
                    <div className="flex gap-1 mt-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                        onClick={() => openEditDialog(expense)}
                      >
                        <i className="fas fa-edit text-xs"></i>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                        onClick={() => handleDeleteExpense(expense)}
                      >
                        <i className="fas fa-trash text-xs"></i>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            className="h-8 text-xs"
          >
            <i className="fas fa-chevron-left"></i>
          </Button>
          <span className="text-xs text-gray-600">
            {pagination.page} / {pagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === pagination.total_pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            className="h-8 text-xs"
          >
            <i className="fas fa-chevron-right"></i>
          </Button>
        </div>
      )}

      {/* Add Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto max-sm:!inset-x-0 max-sm:!top-auto max-sm:!bottom-0 max-sm:!transform-none max-sm:!w-auto max-sm:!max-w-none max-sm:!max-h-[92vh] max-sm:!rounded-b-none max-sm:!rounded-t-2xl max-sm:data-[state=open]:!slide-in-from-bottom max-sm:data-[state=closed]:!slide-out-to-bottom">
          <DialogHeader>
            <DialogTitle className="text-base">Add Expense</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Category *</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-5 text-[10px] text-teal-600"
                  onClick={() => setShowAddCategoryDialog(true)}
                >
                  <i className="fas fa-plus mr-1"></i> Add Custom
                </Button>
              </div>
              <Select
                value={form.category === 'custom' && form.custom_category ? `custom:${form.custom_category}` : form.category}
                onValueChange={(v) => {
                  if (typeof v === 'string' && v.startsWith('custom:')) {
                    // Custom category — extract its display name
                    const customName = v.slice('custom:'.length);
                    setForm(prev => ({ ...prev, category: 'custom', custom_category: customName }));
                  } else {
                    // Predefined — clear any stale custom_category
                    setForm(prev => ({ ...prev, category: v, custom_category: '' }));
                  }
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => {
                    const itemValue = cat.is_predefined ? cat.value : `custom:${cat.custom_value || cat.label}`;
                    return (
                      <SelectItem key={itemValue} value={itemValue}>
                        <span className="flex items-center gap-2">
                          <i className={`fas ${CATEGORY_ICONS[cat.value] || CATEGORY_ICONS.custom} text-xs`}></i>
                          {cat.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {form.category === 'custom' && (
              <div>
                <Label className="text-xs">Custom Category Name *</Label>
                <Input
                  placeholder="Enter custom category"
                  value={form.custom_category}
                  onChange={(e) => setForm(prev => ({ ...prev, custom_category: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            )}
            
            <div>
              <Label className="text-xs">Description *</Label>
              <Textarea
                placeholder="Expense description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="text-sm min-h-[60px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm(prev => ({ ...prev, payment_mode: v }))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map(mode => (
                      <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Vendor/Payee</Label>
                <Input
                  placeholder="Vendor name"
                  value={form.vendor_name}
                  onChange={(e) => setForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Reference Number</Label>
              <Input
                placeholder="Transaction/Bill reference"
                value={form.reference_number}
                onChange={(e) => setForm(prev => ({ ...prev, reference_number: e.target.value }))}
                className="h-8 text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }} className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleAddExpense} className="h-8 text-xs bg-teal-600 hover:bg-teal-700">
              <i className="fas fa-plus mr-1"></i> Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Expense</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value + (cat.custom_value || '')} value={cat.is_predefined ? cat.value : 'custom'}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {form.category === 'custom' && (
              <div>
                <Label className="text-xs">Custom Category Name *</Label>
                <Input
                  placeholder="Enter custom category"
                  value={form.custom_category}
                  onChange={(e) => setForm(prev => ({ ...prev, custom_category: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            )}
            
            <div>
              <Label className="text-xs">Description *</Label>
              <Textarea
                placeholder="Expense description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                className="text-sm min-h-[60px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm(prev => ({ ...prev, payment_mode: v }))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map(mode => (
                      <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Vendor/Payee</Label>
                <Input
                  placeholder="Vendor name"
                  value={form.vendor_name}
                  onChange={(e) => setForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs">Reference Number</Label>
              <Input
                placeholder="Transaction/Bill reference"
                value={form.reference_number}
                onChange={(e) => setForm(prev => ({ ...prev, reference_number: e.target.value }))}
                className="h-8 text-sm"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); setSelectedExpense(null); }} className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleEditExpense} className="h-8 text-xs bg-teal-600 hover:bg-teal-700">
              <i className="fas fa-save mr-1"></i> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Category Dialog */}
      <Dialog open={showAddCategoryDialog} onOpenChange={setShowAddCategoryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Add Custom Category</DialogTitle>
          </DialogHeader>
          
          <div className="py-2">
            <Label className="text-xs">Category Name *</Label>
            <Input
              placeholder="e.g., Equipment, Insurance"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddCategoryDialog(false); setNewCategoryName(''); }} className="h-8 text-xs">
              Cancel
            </Button>
            <Button onClick={handleAddCategory} className="h-8 text-xs bg-teal-600 hover:bg-teal-700">
              <i className="fas fa-plus mr-1"></i> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog — rename / deactivate custom expense categories */}
      <Dialog open={showManageCategoriesDialog} onOpenChange={setShowManageCategoriesDialog}>
        <DialogContent className="sm:max-w-[560px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Manage Custom Categories</DialogTitle>
            <DialogDescription className="text-xs">
              Rename or deactivate custom expense categories. Predefined categories are read-only. Past expenses keep their original category name on deactivation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Predefined section */}
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Predefined ({categories.filter(c => c.is_predefined).length})</h4>
              <div className="space-y-1">
                {categories.filter(c => c.is_predefined).map(cat => (
                  <div key={cat.value} className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50 text-xs text-slate-700">
                    <i className={`fas ${CATEGORY_ICONS[cat.value] || 'fa-tag'} text-[11px] text-slate-400`}></i>
                    <span className="flex-1">{cat.label}</span>
                    <span className="text-[10px] text-slate-400 italic">read-only</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Custom ({categories.filter(c => !c.is_predefined).length})
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[11px] text-teal-600"
                  onClick={() => { setShowManageCategoriesDialog(false); setShowAddCategoryDialog(true); }}
                  data-testid="manage-add-new-category"
                >
                  <i className="fas fa-plus mr-1"></i> Add new
                </Button>
              </div>
              {categories.filter(c => !c.is_predefined).length === 0 ? (
                <div className="text-xs text-slate-400 italic px-2 py-3 bg-slate-50 rounded text-center">
                  No custom categories yet. Click "Add new" to create one.
                </div>
              ) : (
                <div className="space-y-1">
                  {categories.filter(c => !c.is_predefined).map(cat => (
                    <div
                      key={cat.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 hover:border-slate-300 text-xs"
                      data-testid={`manage-category-row-${cat.id}`}
                    >
                      <i className="fas fa-tag text-[11px] text-amber-500"></i>
                      {editingCategoryId === cat.id ? (
                        <>
                          <Input
                            value={editingCategoryName}
                            onChange={(e) => setEditingCategoryName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCategory(cat.id); }}
                            className="h-7 text-xs flex-1"
                            autoFocus
                            data-testid={`rename-input-${cat.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-1 text-emerald-600"
                            onClick={() => handleRenameCategory(cat.id)}
                            data-testid={`save-rename-${cat.id}`}
                          >
                            <i className="fas fa-check text-[11px]"></i>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-1 text-slate-500"
                            onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }}
                          >
                            <i className="fas fa-times text-[11px]"></i>
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-slate-700">{cat.label}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-blue-600 hover:bg-blue-50"
                            onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryName(cat.label); }}
                            data-testid={`rename-btn-${cat.id}`}
                            title="Rename"
                          >
                            <i className="fas fa-pencil-alt text-[10px]"></i>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCategory(cat)}
                            data-testid={`delete-btn-${cat.id}`}
                            title="Deactivate"
                          >
                            <i className="fas fa-trash text-[10px]"></i>
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowManageCategoriesDialog(false); setEditingCategoryId(null); setEditingCategoryName(''); }}
              className="h-8 text-xs"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
